/**
 * Sous-formulaire — branding tenant.
 *
 * 6 couleurs hex — primary, primary_foreground, bg, surface, border, text.
 * Chaque couleur est un `<input type="color">` doublé d'un text input pour
 * la saisie hex précise. La preview live se fait via le hook applyBranding
 * dans la page parent — on n'a rien à faire ici, on push juste l'état.
 *
 * Notes :
 *  - logo_url n'est PAS dans ce composant : il vit dans TenantInfoForm.
 *  - On accepte `null` en entrée (= branding non défini) → on initialise
 *    chaque champ à vide. Cas typique : nouveau tenant ou après reset.
 */

import { useTranslation } from "react-i18next";

import { type Branding } from "../../lib/branding";

/** Clés couleur — sous-ensemble strict de Branding hors `logo_url`. */
type ColorKey = "primary" | "primary_foreground" | "bg" | "surface" | "border" | "text";

const COLOR_KEYS: readonly ColorKey[] = [
  "primary",
  "primary_foreground",
  "bg",
  "surface",
  "border",
  "text",
] as const;

type Props = {
  value: Branding | null;
  onChange: (next: Branding | null) => void;
  disabled?: boolean;
};

export function BrandingForm({ value, onChange, disabled }: Props) {
  const { t } = useTranslation();

  const set = (key: ColorKey, v: string) => {
    const next: Branding = { ...(value ?? {}) };
    if (v === "") {
      delete next[key];
    } else {
      // Note : on stocke la valeur même si elle n'est pas encore une hex
      // 6-digits valide (ex. saisie en cours "#ff"). Le helper applyBranding
      // côté preview ignore les non-hex (couleur retombe à l'initial). Au
      // submit, sanitizeBranding() drop les non-hex avant PATCH.
      next[key] = v;
    }
    onChange(Object.keys(next).length === 0 ? null : next);
  };

  return (
    <div>
      <p className="mb-4 text-sm text-slate-600">{t("settings.branding.subtitle")}</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {COLOR_KEYS.map((key) => {
          const v = value?.[key] ?? "";
          // <input type="color"> exige une vraie hex 6-digits. Si v vide ou
          // invalide, on lui donne un placeholder neutre pour ne pas crasher
          // le picker — la valeur effective côté form reste "".
          const colorValue = /^#[0-9a-fA-F]{6}$/.test(v) ? v : "#000000";
          return (
            <div key={key}>
              <label className="label" htmlFor={`branding-${key}`}>
                {t(`settings.branding.${key}`)}
              </label>
              <div className="flex items-center gap-2">
                <input
                  id={`branding-${key}-picker`}
                  type="color"
                  value={colorValue}
                  onChange={(e) => set(key, e.target.value)}
                  disabled={disabled}
                  className="h-9 w-12 cursor-pointer rounded border border-slate-300"
                  aria-label={t(`settings.branding.${key}`)}
                />
                <input
                  id={`branding-${key}`}
                  type="text"
                  value={v}
                  onChange={(e) => set(key, e.target.value)}
                  disabled={disabled}
                  placeholder="#0066cc"
                  pattern="^#[0-9a-fA-F]{6}$"
                  maxLength={7}
                  className="input flex-1 font-mono text-xs"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
